import {ANON_ROLE_UID, defaultPropertyDefinitions} from "@buildwithflux/constants";
import {
    documentConfigUid,
    getManufacturerPartNumberProperty,
    getManufacturerPropertyValueAsString,
    getProfileUrlForDocumentOwner,
    getProjectExpressionEvaluationContext,
    getPropertyValueAsString,
    getTopLevelFootprints,
    getTopLevelLayouts,
    IDocumentConfigsMap,
} from "@buildwithflux/core";
import type {ElectricalContextDocumentDataSubsetSubset, MechanicalContextDocumentDataSubset} from "@buildwithflux/core";
import type {
    DocumentPermissionsData,
    IExtractedProperty,
    IPropertyData,
    IPropertiesMap,
    PartSourcingProperties,
    PropertySpecialState,
} from "@buildwithflux/models";
import {isPublicDocument} from "@buildwithflux/models";
import {Records} from "@buildwithflux/shared";
import {createSelector} from "reselect";

import {PartStorageHelper} from "../../../../storage_engine/helpers/PartStorageHelper";
import {computeTerminalToNetIdMap} from "../pcb/derived";
import type {DocumentSelector, DocumentStoreState, ParameterizedSelector} from "../types";

/**
 * Derived selectors for document metadata - computed values
 * These selectors perform filtering, mapping, or other computations on document data
 * Note: These use reselect for memoization of expensive computations
 */

/** Checks if the document is loaded */
export const isLoaded: DocumentSelector<boolean> = (state: DocumentStoreState) => state.document !== null;

/** Checks if the document is loading (not yet loaded) */
export const isLoading: DocumentSelector<boolean> = (state: DocumentStoreState) => state.document === null;

/** Checks if the document is published (belongs to a part) */
export const isPublished: DocumentSelector<boolean> = (state: DocumentStoreState) => !!state.belongs_to_part_uid;

/** Checks if the document is a local component */
export const isLocalComponent: DocumentSelector<boolean> = (state: DocumentStoreState) => !!state.localToDocumentUid;

/** Gets the document owner's profile URL - memoized */
export const ownerProfileUrl: DocumentSelector<string | undefined> = createSelector(
    [
        (state: DocumentStoreState) => state.owner_handle,
        (state: DocumentStoreState) => state.enterprise_owner_uid,
        (state: DocumentStoreState) => state.enterprise_owner_handle,
        (state: DocumentStoreState) => state.organization_owner_is_enterprise_default,
    ],
    (
        ownerHandle,
        enterpriseOwnerUid,
        enterpriseOwnerHandle,
        organizationOwnerIsEnterpriseDefault,
    ): string | undefined => {
        if (!ownerHandle) {
            return undefined;
        }
        return getProfileUrlForDocumentOwner({
            owner_handle: ownerHandle,
            enterprise_owner_uid: enterpriseOwnerUid,
            enterprise_owner_handle: enterpriseOwnerHandle,
            organization_owner_is_enterprise_default: organizationOwnerIsEnterpriseDefault,
        });
    },
);

/** Checks if the document has any elements */
export const hasElements: DocumentSelector<boolean> = (state: DocumentStoreState) => {
    const elements = state.elements;
    if (!elements) return false;
    return !Records.isEmpty(elements);
};

/** Checks if the document has any nets */
export const hasNets: DocumentSelector<boolean> = (state: DocumentStoreState) => {
    const nets = state.nets;
    if (!nets) return false;
    return !Records.isEmpty(nets);
};

/** Checks if the document has any routes */
export const hasRoutes: DocumentSelector<boolean> = (state: DocumentStoreState) => {
    const routes = state.routes;
    if (!routes) return false;
    return !Records.isEmpty(routes);
};

/** Checks if the document has any comment threads */
export const hasCommentThreads: DocumentSelector<boolean> = (state: DocumentStoreState) => {
    const commentThreads = state.comment_threads;
    if (!commentThreads) return false;
    return !Records.isEmpty(commentThreads);
};

/** Checks if the document has any PCB layout nodes */
export const hasPcbLayoutNodes: DocumentSelector<boolean> = (state: DocumentStoreState) => {
    const pcbLayoutNodes = state.pcbLayoutNodes;
    if (!pcbLayoutNodes) return false;
    return !Records.isEmpty(pcbLayoutNodes);
};

/** Checks if the document has any PCB layout rule sets */
export const hasPcbLayoutRuleSets: DocumentSelector<boolean> = (state: DocumentStoreState) => {
    const pcbLayoutRuleSets = state.pcbLayoutRuleSets;
    if (!pcbLayoutRuleSets) return false;
    return !Records.isEmpty(pcbLayoutRuleSets);
};

/** Gets the anonymous role from the document roles */
export const anonRole: DocumentSelector<DocumentPermissionsData | undefined> = (state: DocumentStoreState) =>
    state.roles?.[ANON_ROLE_UID];

/**
 * Gets the manufacturer part number property from the document
 * Returns the property object containing the MPN value
 */
export const manufacturerPartNumber: DocumentSelector<
    IPropertyData | IExtractedProperty | PropertySpecialState | undefined
> = (state: DocumentStoreState) => {
    const properties = state.properties;
    if (!properties) return undefined;
    return getManufacturerPartNumberProperty(properties);
};

/**
 * Checks if a document has "advanced" permissions.
 *
 * Advanced permissions means any of:
 *  - For an enterprise document, the document is owned by a non-default organization within the enterprise.
 *  - For an organization document, the document has any permissions set for any user that is not a member of the
 *     organization, including the anonymous user.
 *  - For a non-organization document, at least one user has a permission level that differs from the anonymous user.
 *     This state is accessible if, for example, a user sets their document to private and then adds a specific user
 *     with permissions greater than "none".
 *  - There is at least one pending invite that has been sent by email.
 */
export const hasAdvancedPermissions: DocumentSelector<boolean> = (state: DocumentStoreState) => {
    if (state.document === null) {
        return false;
    }

    // For an enterprise document, the document is owned by a non-default organization within the enterprise
    if (state.enterprise_owner_uid && !state.organization_owner_is_enterprise_default) {
        return true;
    }

    // For an organization document, check if it's public (has anon role with permissions)
    if (state.organization_owner_uid && isPublicDocument(state.roles ?? {})) {
        return true;
    }

    // Check if any user (other than anon and owner) has permissions
    const roles = state.roles ?? {};
    for (const userUid of Object.keys(roles)) {
        if (userUid !== ANON_ROLE_UID && userUid !== state.owner_uid) {
            return true;
        }
    }

    // Check if there are any pending invites
    const invites = state.invites ?? {};
    for (const invite of Object.values(invites)) {
        if (invite.status === "created") {
            return true;
        }
    }

    return false;
};

/** Gets top-level PCB layout nodes - memoized */
export const topLevelPcbLayoutNodes = createSelector(
    [(state: DocumentStoreState) => state.pcbLayoutNodes],
    (pcbLayoutNodes) => {
        if (!pcbLayoutNodes) {
            return [];
        }
        return getTopLevelLayouts(pcbLayoutNodes);
    },
);

/** Gets top-level PCB footprint nodes - memoized */
export const topLevelPcbFootprintNodes = createSelector(
    [(state: DocumentStoreState) => state.pcbLayoutNodes],
    (pcbLayoutNodes) => {
        if (!pcbLayoutNodes) {
            return [];
        }
        return getTopLevelFootprints(pcbLayoutNodes);
    },
);

/**
 * Count selectors - return primitive numbers to avoid useShallow overhead
 * These are optimized for components that only need to check counts or existence
 */

/** Gets count of top-level PCB layout nodes - memoized */
export const topLevelPcbLayoutNodesCount: DocumentSelector<number> = createSelector(
    [(state: DocumentStoreState) => state.pcbLayoutNodes],
    (pcbLayoutNodes): number => {
        if (!pcbLayoutNodes) {
            return 0;
        }
        return getTopLevelLayouts(pcbLayoutNodes).length;
    },
);

/** Gets count of top-level PCB footprint nodes - memoized */
export const topLevelPcbFootprintNodesCount: DocumentSelector<number> = createSelector(
    [(state: DocumentStoreState) => state.pcbLayoutNodes],
    (pcbLayoutNodes): number => {
        if (!pcbLayoutNodes) {
            return 0;
        }
        return getTopLevelFootprints(pcbLayoutNodes).length;
    },
);

/** Builds a map of terminal UIDs to their containing net UIDs - memoized */
export const terminalToNetUidMap: DocumentSelector<{[key: string]: string}> = createSelector(
    [(state: DocumentStoreState) => state.pcbLayoutNodes],
    (pcbLayoutNodes): {[key: string]: string} => {
        return computeTerminalToNetIdMap(pcbLayoutNodes);
    },
);

/** Gets the net UID that contains a given terminal - parameterized, uses memoized map */
export const containingNetUid: ParameterizedSelector<string, string | undefined> = (uid) => (state) =>
    terminalToNetUidMap(state)[uid];

/** Gets document-level part sourcing properties - memoized */
export const partSourcingProperties: DocumentSelector<PartSourcingProperties | null> = createSelector(
    [
        (state: DocumentStoreState) => state.document,
        (state: DocumentStoreState) => state.name,
        (state: DocumentStoreState) => state.properties,
        (state: DocumentStoreState) => state.belongs_to_part_uid,
    ],
    (document, name, properties, belongsToPartUid): PartSourcingProperties | null => {
        if (document === null) return null;

        const safeProperties = properties ?? {};
        const manufacturerName = getManufacturerPropertyValueAsString(safeProperties) || null;
        const manufacturerPartNumber =
            getPropertyValueAsString(defaultPropertyDefinitions.mpn.label, safeProperties)?.trim() || null;

        return {
            manufacturerName,
            manufacturerPartNumber,
            partName: name ?? null,
            partUid: belongsToPartUid ?? null,
        };
    },
);

/** Gets the mechanical document subset needed for expression evaluation - memoized */
export const mechanicalSubset: DocumentSelector<MechanicalContextDocumentDataSubset | undefined> = createSelector(
    [
        (state: DocumentStoreState) => state.document,
        (state: DocumentStoreState) => state.uid,
        (state: DocumentStoreState) => state.name,
        (state: DocumentStoreState) => state.description,
        (state: DocumentStoreState) => state.properties,
        (state: DocumentStoreState) => state.slug,
        (state: DocumentStoreState) => state.owner_handle,
        (state: DocumentStoreState) => state.elements,
        (state: DocumentStoreState) => state.pcbLayoutNodes,
    ],
    (document, uid, name, description, properties, slug, ownerHandle, elements, pcbLayoutNodes) => {
        if (!document) return undefined;
        return {
            uid: uid ?? "",
            name: name ?? "",
            description: description ?? "",
            properties: properties ?? {},
            slug: slug ?? "",
            owner_handle: ownerHandle ?? "",
            elements: elements ?? {},
            pcbLayoutNodes: pcbLayoutNodes ?? {},
        };
    },
);
/** Gets the 3D model asset storage name from the document assets */
export const document3DModelAssetStorageName: DocumentSelector<string | undefined> = (
    state: DocumentStoreState,
): string | undefined => PartStorageHelper.get3DModelAssetFromAssets(state.assets ?? {});

/** Gets the document package code from properties */
export const documentPackageCode: DocumentSelector<string | undefined> = (
    state: DocumentStoreState,
): string | undefined => PartStorageHelper.getPackageCodeFromProperties(state.properties ?? {});

/** Gets the project expression context from the document - memoized */
export const projectExpressionContext = createSelector([(state: DocumentStoreState) => state.document], (document) => {
    if (document === null) return undefined;
    return getProjectExpressionEvaluationContext(document);
});

/**
 * Pure function to compute the electrical document subset needed for expression evaluation.
 * @param uid The document UID
 * @param description The document description
 * @param name The document name
 * @param ownerHandle The document owner handle
 * @param slug The document slug
 * @param properties The document properties
 * @returns The electrical document subset or undefined
 */
export function computeElectricalDocumentSubset(
    uid: string | undefined,
    description: string | undefined,
    name: string | undefined,
    ownerHandle: string | undefined,
    slug: string | undefined,
    properties: IPropertiesMap | undefined,
): ElectricalContextDocumentDataSubsetSubset | undefined {
    if (!uid || !description || !name || !ownerHandle || !slug || !properties) {
        return undefined;
    }
    return {
        uid,
        description,
        name,
        slug,
        owner_handle: ownerHandle,
        properties,
    };
}

// ============================================================================
// Pure compute functions (not selectors - take parameters directly)
// ============================================================================

/**
 * Pure function to compute if a document has "advanced" permissions.
 *
 * Advanced permissions means any of:
 *  - For an enterprise document, the document is owned by a non-default organization within the enterprise.
 *  - For an organization document, the document has any permissions set for any user that is not a member of the
 *     organization, including the anonymous user.
 *  - For a non-organization document, at least one user has a permission level that differs from the anonymous user.
 *     This state is accessible if, for example, a user sets their document to private and then adds a specific user
 *     with permissions greater than "none".
 *  - There is at least one pending invite that has been sent by email.
 *
 * @param document - The document data
 * @returns true if the document has advanced permissions
 */
export function computeDocumentHasAdvancedPermissions(
    document: import("@buildwithflux/models").IDocumentData | null | undefined,
): boolean {
    if (!document) {
        return false;
    }

    // For an enterprise document, the document is owned by a non-default organization within the enterprise
    if (document.enterprise_owner_uid && !document.organization_owner_is_enterprise_default) {
        return true;
    }

    // For an organization document, check if it's public (has anon role with permissions)
    if (document.organization_owner_uid && isPublicDocument(document.roles ?? {})) {
        return true;
    }

    // Check if any user (other than anon and owner) has permissions
    const roles = document.roles ?? {};
    for (const userUid of Object.keys(roles)) {
        if (userUid !== ANON_ROLE_UID && userUid !== document.owner_uid) {
            return true;
        }
    }

    // Check if there are any pending invites
    const invites = document.invites ?? {};
    for (const invite of Object.values(invites)) {
        if (invite.status === "created") {
            return true;
        }
    }

    return false;
}

/**
 * Pure function to compute the document config section with owner default.
 * This merges the owner's settings with the current user's settings, preferring
 * the current user's settings for any conflicts.
 *
 * @param section - The name of the document config section
 * @param currentUserUid - The current user's UID
 * @param ownerUid - The document owner's UID
 * @param documentConfigs - The document configs map
 * @returns An object mapping the setting keys in the given section to their determined values
 */
export function computeDocumentConfigSectionWithOwnerDefault(
    section: string,
    currentUserUid: string | undefined,
    ownerUid: string | undefined,
    documentConfigs: IDocumentConfigsMap | undefined,
): {[key: string]: unknown} {
    if (!documentConfigs) return {};

    const currentUserSettings: Array<[string, unknown]> = [];
    const ownerSettings: Array<[string, unknown]> = [];

    for (const [uid, config] of Object.entries(documentConfigs)) {
        if (currentUserUid && uid.endsWith(documentConfigUid({key: "", section: section, user_uid: currentUserUid}))) {
            currentUserSettings.push([config.key, config.value]);
        } else if (ownerUid && uid.endsWith(documentConfigUid({key: "", section: section, user_uid: ownerUid}))) {
            ownerSettings.push([config.key, config.value]);
        }
    }

    return Object.fromEntries(ownerSettings.concat(currentUserSettings));
}
